import { useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { LandingPagePreview } from '#/domains/landing/components';
import { useLandingPageContent } from '#/domains/landing/hooks/useLandingPageContent';
import { track } from '#/utils/analytics';
import { deriveEventKeys } from '#/domains/landing/utils/slugEventKeys';

const DEFAULT_BROWSER_TITLE = '정부 혜택 알리미';

function landingPageTitleForDocument(raw: string): string {
  return raw
    .replace(/\s*\n\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function LandingPage() {
  const params = useParams();
  const location = useLocation();
  /** Splat from `path="*"`; fallback to pathname if needed across RR versions */
  const slugPath = (
    params['*'] != null && String(params['*']).length > 0
      ? String(params['*'])
      : location.pathname.replace(/^\/+|\/+$/g, '')
  ).replace(/^\/+|\/+$/g, '');

  const { content, loading, error, contentBase } =
    useLandingPageContent(slugPath);

  const pvSentForSlugRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading) {
      document.title = DEFAULT_BROWSER_TITLE;
      return;
    }
    if (content) {
      const pageTitle = landingPageTitleForDocument(content.pageHeader.title);
      // `seoTitle` overrides the document title only. Analytics keeps reporting
      // the visible headline so event history stays comparable.
      document.title = content.seoTitle
        ? landingPageTitleForDocument(content.seoTitle)
        : pageTitle;
      window.dataLayer?.push({
        event: 'virtual_page_view',
        page_title: pageTitle,
        page_slug: slugPath,
      });
      const pvEventKey = deriveEventKeys(slugPath)?.pv;
      if (pvEventKey && pvSentForSlugRef.current !== slugPath) {
        pvSentForSlugRef.current = slugPath;
        track('service_entry_pv', {
          event_key: pvEventKey,
          page_slug: slugPath,
          page_title: pageTitle,
        });
      }
    } else {
      document.title = DEFAULT_BROWSER_TITLE;
    }
  }, [loading, content, slugPath]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-gray-50">
        <p className="text-sm">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 text-gray-60">
        <p className="text-sm">{error}</p>
        <p className="text-xs">
          Run the generator export and{' '}
          <code className="rounded bg-gray-10 px-1">yarn sync:landing</code>{' '}
          from playground-fe.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[640px]">
      <LandingPagePreview
        content={content}
        contentBase={contentBase}
        slugPath={slugPath}
      />
    </div>
  );
}
